import { TempControlSizingResult, TempControlValveFamily } from '__generated-api__';

import { getDigitalSkuForThermostatic, resolveThermostaticMapKey } from 'tempcontrol/utils/thermostatic-digital-map';

export interface ResultProductSlot {
  /** Full model shown on the card subtitle (may include suffixes / manifold). */
  displayModel: string;
  /** SKU used to hydrate catalog data (image, docs, title). */
  catalogSku: string;
  family: TempControlValveFamily;
}

export interface ResolvedResultProducts {
  /** Featured card — digital when a mapping exists, otherwise conventional. */
  recommended: ResultProductSlot;
  /** Alternate card — conventional counterpart when digital is available. */
  alternate: ResultProductSlot | null;
  alternateHeadline: string;
  digital: ResultProductSlot | null;
  conventional: ResultProductSlot;
}

/**
 * Build the recommended/alternate product cards for the results page.
 * Digital is always featured when a mapping exists; the engine's
 * thermostatic part number is the conventional alternate.
 */
export const resolveResultProducts = (result: TempControlSizingResult): ResolvedResultProducts | null => {
  if (!result.part_number) {
    return null;
  }

  const thermostaticPart = stripManifold(result.part_number);
  const thermostaticKey = resolveThermostaticMapKey(result.part_number, result.valve_series);
  const digitalSku = thermostaticKey ? getDigitalSkuForThermostatic(thermostaticKey) : null;

  const conventional: ResultProductSlot = {
    displayModel: thermostaticPart,
    catalogSku: thermostaticKey ?? thermostaticPart,
    family: TempControlValveFamily.Conventional,
  };

  const digital: ResultProductSlot | null = digitalSku
    ? {
        displayModel: digitalSku,
        catalogSku: digitalSku,
        family: TempControlValveFamily.Digital,
      }
    : null;

  if (digital) {
    return {
      recommended: digital,
      alternate: conventional,
      alternateHeadline: '…or choose a Thermostatic version',
      digital,
      conventional,
    };
  }

  return {
    recommended: conventional,
    alternate: null,
    alternateHeadline: '',
    digital: null,
    conventional,
  };
};

/** Part number shown in “Show all data” for the user's valve family choice. */
export const selectedResultPartNumber = (
  resolved: ResolvedResultProducts,
  selectedFamily: TempControlValveFamily
): string => {
  if (selectedFamily === TempControlValveFamily.Digital && resolved.digital) {
    return resolved.digital.displayModel;
  }
  return resolved.conventional.displayModel;
};

export const defaultResultValveFamily = (
  resolved: ResolvedResultProducts,
  savedFamily?: TempControlValveFamily | null
): TempControlValveFamily => {
  if (savedFamily === TempControlValveFamily.Digital || savedFamily === TempControlValveFamily.Conventional) {
    return savedFamily;
  }
  return resolved.digital ? TempControlValveFamily.Digital : TempControlValveFamily.Conventional;
};

const stripManifold = (partNumber: string) => partNumber.replace(/^\[\d+\]\s*/, '').trim();
